import React, { useState, useEffect } from 'react';
import { LogOut, ArrowRight, Edit2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { publicationTermsService, authService } from '../services/api';

function AdminPublicationTerms() {
  const navigate = useNavigate();
  const [termsId, setTermsId] = useState(null);
  const [terms, setTerms] = useState({
    contentRequirements: '',
    copyright: '',
    fees: '',
    republicationPolicy: '',
  });
  const [temp, setTemp] = useState({
    contentRequirements: '',
    copyright: '',
    fees: '',
    republicationPolicy: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await publicationTermsService.get();
        if (response.data && response.data.id) {
          setTermsId(response.data.id);
          setTerms({
            contentRequirements: response.data.contentRequirements || '',
            copyright: response.data.copyright || '',
            fees: response.data.fees || '',
            republicationPolicy: response.data.republicationPolicy || '',
          });
        } else {
          setEditing(true);
        }
      } catch (err) {
        setEditing(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleEdit = () => {
    setTemp({ ...terms });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      if (termsId) {
        await publicationTermsService.update(termsId, temp);
      } else {
        const response = await publicationTermsService.create(temp);
        setTermsId(response.data.id);
      }
      setTerms({ ...temp });
      setEditing(false);
      setError('');
    } catch (err) {
      setError('خطا در ذخیره شرایط انتشار');
    }
  };

  const handleCancel = () => {
    setTemp({ ...terms });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  const fields = [
    { key: 'contentRequirements', label: 'الزامات محتوایی', rows: 6 },
    { key: 'copyright', label: 'حقوق نشر و مالکیت', rows: 6 },
    { key: 'fees', label: 'هزینه‌های انتشار', rows: 6 },
    { key: 'republicationPolicy', label: 'سیاست بازنشر', rows: 6 },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت شرایط انتشار</h1>
              <p className="text-xl text-blue-100">ویرایش محتوای صفحه شرایط انتشار</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {editing ? (
          <div className="bg-white rounded-xl shadow-md p-8 space-y-6">
            {fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-bold text-gray-900 mb-2">{field.label}</label>
                <textarea
                  value={temp[field.key]}
                  onChange={(e) => setTemp({ ...temp, [field.key]: e.target.value })}
                  rows={field.rows}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            ))}
            <div className="flex gap-4">
              <button onClick={handleSave} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
              <button onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {fields.map(field => (
              <section key={field.key} className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{field.label}</h2>
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {terms[field.key] || 'اطلاعاتی برای نمایش وجود ندارد'}
                </div>
              </section>
            ))}
            <button onClick={handleEdit} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">
              <Edit2 size={18} />
              ویرایش
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPublicationTerms;
